import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { LogOut, User as UserIcon, DoorOpen, Shield, Bell, BellOff, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { enablePushNotifications, disablePushNotifications, getPushSubscription, pushSupported } from "@/lib/push";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    getPushSubscription().then((s) => setPushOn(!!s));
    // Handle navigation requests from the service worker (notification click)
    const handler = (ev) => {
      if (ev.data?.type === "navigate" && ev.data.url) navigate(ev.data.url);
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => navigator.serviceWorker?.removeEventListener("message", handler);
  }, [navigate]);

  const togglePush = async () => {
    try {
      if (pushOn) {
        await disablePushNotifications();
        setPushOn(false);
        toast.success("Notifications off");
      } else {
        await enablePushNotifications();
        setPushOn(true);
        toast.success("Notifications on — we'll ping you when your mini rooms light up");
      }
    } catch (e) {
      toast.error(e.message || "Could not toggle notifications");
    }
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/lobby" className="flex items-center gap-2 group" data-testid="brand-link">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#e81cff] to-[#ff2a5f] shadow-[0_0_20px_rgba(232,28,255,0.5)] group-hover:shadow-[0_0_30px_rgba(232,28,255,0.8)] transition-all hue-spin" />
          <span className="font-display font-black tracking-tighter text-xl">Xhat</span>
        </Link>

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-white/5 transition-colors">
              <Avatar user={user} size={32} />
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-sm font-semibold">{user.username}</span>
                {user.is_guest && <span className="text-[10px] tracking-wider uppercase text-fuchsia-400">Guest</span>}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121215] border-white/10 text-white min-w-[180px]">
              <DropdownMenuLabel className="text-zinc-400 text-xs uppercase tracking-wider">Signed in</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="cursor-pointer focus:bg-white/5">
                <UserIcon className="w-4 h-4 mr-2" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/lobby")} data-testid="menu-lobby" className="cursor-pointer focus:bg-white/5">
                <DoorOpen className="w-4 h-4 mr-2" /> Lobby
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dms")} data-testid="menu-dms" className="cursor-pointer focus:bg-white/5">
                <MessageCircle className="w-4 h-4 mr-2" /> Direct messages
              </DropdownMenuItem>
              {pushSupported() && (
                <DropdownMenuItem onClick={togglePush} data-testid="menu-notifications" className="cursor-pointer focus:bg-white/5">
                  {pushOn ? <BellOff className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                  {pushOn ? "Mute notifications" : "Enable notifications"}
                </DropdownMenuItem>
              )}
              {user.role === "admin" && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-dashboard" className="cursor-pointer focus:bg-white/5">
                    <Shield className="w-4 h-4 mr-2" /> Admin · Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/downlow")} data-testid="menu-admin" className="cursor-pointer focus:bg-white/5">
                    <Shield className="w-4 h-4 mr-2" /> Admin · DownLow
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem
                data-testid="menu-logout"
                onClick={() => { logout(); navigate("/"); }}
                className="cursor-pointer text-rose-400 focus:bg-white/5 focus:text-rose-300"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
